import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, Label, Avatar } from '../components';
import { Button } from 'app/components';
import Infocard from '../../../components/info-card/infoCard';
import { Calendar, Info, Plus, Sparkle } from '@phosphor-icons/react';
import DetailsEdit from './detailsEdit';
import UploadModal from '../../event-create/components/step-4/uploadModal';
import { ADDRESS_FIELDS, ORG_KEY_FIELD_MAPPING } from '../constants';
import { useSelector } from 'react-redux';
import { getBusinessConfig, updateBusinessConfig } from '../api';
import { uploadImage } from '../../event-create/api';
import { generateUniqueId, updateImageId } from '../../../utils/commonUtils';
import ViewProofsModal from '../../event-create/components/step-4/viewProofsModal';
import { IMAGE_TAGS } from '../../../constants/imageTags';

const Organization = ({ isAdmin, organizerId, stateName, reducerPrefix }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [fieldsToEdit, setFieldsToEdit] = useState([]);
  const [editButtonIndex, setEditButtonIndex] = useState(0);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [currentUploadField, setCurrentUploadField] = useState(null);
  const [viewProofs, setViewProofs] = useState(false);

  const navigate = useNavigate();

  const { userData } = useSelector((state) => state.app);
  const { businessConfig = {}, eventsCount } = useSelector((state) => state[stateName || 'eventList']);
  const { live: liveEvents, past: pastEvents } = eventsCount || {};

  // const shouldAllowEdit = useMemo(() => (!liveEvents?.length && !pastEvents?.length) || isAdmin, [liveEvents, pastEvents]);
  const shouldAllowEdit = true;

  useEffect(() => {
    const id = organizerId || userData?.business_config_id;
    if (id && (businessConfig?.business_config_id !== id)) getBusinessConfig(id, reducerPrefix);
  }, [userData?.business_config_id, organizerId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleEditClick = (fields, index) => {
    setFieldsToEdit(fields);
    setEditButtonIndex(index);
    const initialFormData = fields.reduce((acc, field) => {
      if (ADDRESS_FIELDS.includes(field)) acc.address_details = businessConfig?.address_details;
      else {
        const key = ORG_KEY_FIELD_MAPPING[field] || field;
        acc[key] = businessConfig[key];
      }
      return acc;
    }, {});
    console.log(initialFormData);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleSave = () => {
    const id = organizerId || userData?.business_config_id;
    const fieldsToSave = fieldsToEdit.filter(field => !['aadhar', 'gst'].includes(field));
    const payload = {
      ...businessConfig,
      ...fieldsToSave.reduce((acc, field) => {
        const key = ORG_KEY_FIELD_MAPPING[field] || field;
        if (ADDRESS_FIELDS.includes(key)) return { ...acc, address_details: { ...acc?.address_details, ...formData?.address_details, [key]: formData?.address_details?.[key] } };
        else return {...acc, [key]: formData[key]};
      }, {}),
    };
    updateBusinessConfig(id, payload, reducerPrefix, () => {
      setModalOpen(false);
      // Check which field was edited to open corresponding upload modal
      const uploadField = fieldsToEdit.find(field => ['aadhar', 'gst'].includes(field));
      if (uploadField) {
        setCurrentUploadField(uploadField);
        setUploadModalOpen(true);
      }
    });
  };

  const handleChange = (e, field) => {
    const { value } = e.target;
    const pascalCasedValue = field === 'contact_first_name' || field === 'company_name'
      ? value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
      : value;
    if (ADDRESS_FIELDS.includes(field)) {
      setFormData({ ...formData, address_details: { ...formData?.address_details, [field]: pascalCasedValue } });
    } else setFormData({ ...formData, [field]: pascalCasedValue });
  };

  const handleStateChange = (state) => {
    setFormData({ ...formData, address_details: { ...formData?.address_details, state } });
  };

  const handleUploadSubmit = (file) => {
    const id = organizerId || userData?.business_config_id;
    // Handle file upload and update corresponding field if needed
    console.log(`Uploaded ${currentUploadField} file:`, file);
    const promise = uploadImage({ image: file, tag: IMAGE_TAGS.AADHAR_FRONT, fileName: `${userData?.smp_user_id || userData?._id || generateUniqueId()}_${IMAGE_TAGS.AADHAR_FRONT}`, loaderId: 'org-proof-upload' });
    promise.then((resp) => {
      const imageId = resp?.data?.data?.image_id;
      const payload = {
        ...businessConfig,
        ...formData,
        is_gst_registered: currentUploadField === 'gst',
        proof_image: imageId,
      };
      updateBusinessConfig(id, payload, reducerPrefix, () => {
        setUploadModalOpen(false);
      });
    });
  };

  const formatAadharNumber = (value) => {
    const formattedValue = value?.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3');
    return formattedValue;
  }

  const onSaveAvatar = (avatar, cb = () => null) => {
    const id = organizerId || userData?.business_config_id;
    const callback = (resp) => {
      const payload = {
        ...businessConfig,
        ...formData,
        logo: resp?.data?.data?.url,
      };
      updateBusinessConfig(id, payload, reducerPrefix, () => {
        cb();
      });
    };
    uploadImage({ image: avatar, tag: IMAGE_TAGS.ORGANIZER_PROFILE, fileName: `${userData?._id || generateUniqueId()}_${IMAGE_TAGS.ORGANIZER_PROFILE}` }).then(callback);
  };

  const viewProofCta = (
    <Button
      type='tertiary-text-underlined'
      onClick={() => setViewProofs(true)}
    >
      View uploaded proofs
    </Button>
  );

  return (
    <div className='pb-4'>
      <div className='heading-24 pb-4'> Organization details </div>

      {(!userData?.business_config_id && !isAdmin) ? (
        <Banner
          icon={<Calendar size={32} />}
          title='Create an event to add details'
          cta='List your event'
          ctaIcon={<Plus size={24} />}
          ctaType='primary-black'
          onClick={() => navigate('../event/create', { state: { from: window.location.pathname } })}
        />
      ) : (
        <div className='flex flex-col gap-6 items-start w-full overflow-auto'>
          <Avatar
            businessConfig={businessConfig}
            onSaveAvatar={onSaveAvatar}
          />
          {businessConfig && (
            <>
              <Label
                title='Display name'
                content={businessConfig.contact_first_name}
                button={ 'Edit'}
                onButtonClick={() => handleEditClick(['contact_first_name', 'note'], 0)}
              />
              <Label
                title='Bio'
                content={!businessConfig.note ? 'Add bio' : businessConfig.note}
              />
              {isAdmin && (
                <>
                  <Label
                    title='Email ID'
                    content={businessConfig.email_id}
                  />
                  <Label
                    title='Phone Number'
                    content={businessConfig.phone_number}
                  />
                </>
              )}
              <hr className='accent-grey-5 w-full my-8' />
              <Label
                title='Registered Company name/Organizer’s name'
                content={businessConfig.company_name}
                button={shouldAllowEdit && 'Edit'}
                onButtonClick={() => handleEditClick(['company_name', 'address_line_1', 'state'], 1)}
              />
              <Label
                title='Address'
                content={businessConfig?.address_details?.address_line_1}
              />
              <Label
                title='State'
                content={businessConfig?.address_details?.state}
              />
              {!businessConfig?.is_gst_registered ? (
                <>
                  <hr className='accent-grey-5 w-full my-8' />
                  <Label
                    title='PAN number'
                    content={formatAadharNumber(businessConfig?.proof_data)}
                    button={shouldAllowEdit && 'Edit'}
                    onButtonClick={() => handleEditClick(['pan'], 3)}
                    cta={viewProofCta}
                  />
                </>
              ) : (
                <Label
                  title='PAN number'
                  content={businessConfig?.alternate_proof_data}
                  button={shouldAllowEdit && 'Edit'}
                  onButtonClick={() => handleEditClick(['pan_registered'], 3)}
                />
              )}
              {(businessConfig?.is_gst_registered && businessConfig?.proof_data) &&
                <>
                  <hr className='accent-grey-5 w-full my-8' />
                  <Label
                    title='GSTIN number'
                    content={businessConfig.proof_data}
                    button={shouldAllowEdit && 'Edit'}
                    onButtonClick={() => handleEditClick(['gst'], 4)}
                    cta={viewProofCta}
                  /> 
                </>}
                  <hr className='accent-grey-5 w-full my-8' />
                  <Label title='Account Holder Name'
                    content={businessConfig?.payment_details?.account_holder_name || 'Not added'}
                  />
                  <Label title='Account Number'
                    content={businessConfig?.payment_details?.account_number || 'Not added'}
                  />
                  <Label title='IFSC Code'
                    content={businessConfig?.payment_details?.ifsc_code || 'Not added'}
                  />
                  <Label title='Bank Name'
                    content={businessConfig?.payment_details?.bank_name || 'Not added'}
                  />
              {/* {!businessConfig?.is_gst_registered ? (
                <Infocard
                  icon={<Info size={24} />}
                  content={<div className='text-16'>
                    <a className='font-semibold underline cursor-pointer'
                      onClick={()=> {
                        handleEditClick(['gst'],4);
                        setModalOpen(true);
                      }}
                    >Update your GST details </a>
                    to avail tax benefits.
                  </div>}
                />
              ) : (
                formData.gst ? (
                  <Infocard
                    className='w-fit bg-light-green'
                    icon={<Sparkle size={24} 
                      color='#009D64'
                      weight='fill' />}
                    content={<div className='text-16 text-primary-green'>GST details have been successfully submitted and is awaiting review.    </div>}
                  />
                ) : (
                  <Infocard
                    className='w-fit'
                    icon={<Info size={24} />}
                    content={<div className='text-16'>Reach out to our support team if you want to update your GST details.
                      <a className='ml-0.5 font-semibold underline'>Get in touch </a>
                    </div>}
                  />
                ))} */}
            </>
          )}
        </div>
      )}
      <DetailsEdit
        isOpen={isModalOpen}
        handleSave={handleSave}
        onClose={() => setModalOpen(false)}
        formData={formData}
        onChangeData={handleChange}
        handleStateChange={handleStateChange}
        fieldsToEdit={fieldsToEdit}
        editButtonIndex={editButtonIndex}
        formatAadharNumber={formatAadharNumber}
      />
      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setModalOpen(true);
          setFieldsToEdit([currentUploadField]);
        }}
        uploadTitle={currentUploadField === 'aadhar' ? <div className='flex flex-col gap-2'>Upload images of your Aadhar Card
          <div className='text-14'>Make sure the images clearly shows your face. </div>
        </div> : 'Upload your GST certificate'}
        uploadFileName={`Upload your ${currentUploadField} `}
        onSubmit={handleUploadSubmit}
        reuploadText="Reupload file"
      />
      <ViewProofsModal
        isOpen={viewProofs}
        onClose={() => setViewProofs(false)}
        config={businessConfig}
        reducerPrefix={reducerPrefix}
        allowEdit={shouldAllowEdit || isAdmin}
      />
    </div>
  );
};

export default Organization;
